import type { DraftJobProps } from '@/features/jobs/drafting/DraftJob'
import {
  type Job as JobProto,
  Status as StatusProto,
} from '@taxfyle/api-internal/internal/job_pb'
import type { GetQuoteResponse } from '@taxfyle/api-internal/internal/draft_pb'
import type { PrimeWorkspaceFn } from '@/features/iam'
import { Id } from '@/infra/id'
import { JobLegend } from '@/features/jobs/job-legend'
import {
  mapJobProtoToTitle,
  mapLegendToProps,
} from '@/features/jobs/job-mappers'
import type { JobIntakeResponse } from '@/features/jobs/job-intake-response'
import type { Quote } from '@/features/jobs/drafting/Quote'
import { safeTimestampToDate, safeDecimalToNumber } from '@/utils/grpc-util'

/**
 * Maps a job proto to draft props.
 *
 * @param primeWorkspace Function to prime workspace model
 * @param proto Job proto to map
 */
export function mapJobProtoToDraftJobProps(
  primeWorkspace: PrimeWorkspaceFn,
  proto: JobProto,
): DraftJobProps {
  const legend = proto.getLegend()
  const couponIds = proto.getCouponIdsList() ?? []
  const answersList = proto.getAnswersList()

  const answers: JobIntakeResponse[] = answersList.map((answer) => ({
    id: Id.assert(answer.getId()),
    valueJson: answer.getValue(),
  }))

  return {
    id: Id.assert(proto.getId()),
    title: mapJobProtoToTitle(proto),
    legendId: legend?.getId() ? Id.assert(legend.getId()) : Id.none,
    legendName: legend?.getName() ?? '',
    workspace: primeWorkspace({ id: proto.getWorkspaceId() }),
    createdAt: safeTimestampToDate(proto.getDateCreated()),
    isSubmitted: proto.getStatus() !== StatusProto.UNDER_CONSTRUCTION,
    answers,
    couponId: couponIds[0] ?? null,
    legend: legend ? mapLegendToProps(legend) : JobLegend.none,
    requestedProviderUserId:
      proto.getRequestedProPublicId()?.getValue() ?? null,
    paidAmount: safeDecimalToNumber(proto.getPaidAmount()),
  }
}

/**
 * Maps a GetQuoteResponse proto to a Quote.
 *
 * @param proto GetQuoteResponse proto to map
 */
export function mapGetQuoteResponseToQuote(proto: GetQuoteResponse): Quote {
  const coupon = proto.getCoupon()
  const jobScopeItems = proto.getJobScopeItemNamesList()

  const primaryAmount = safeDecimalToNumber(proto.getPrimaryAmount())
  const upfrontAmount = safeDecimalToNumber(proto.getAmountDueNow())
  const total = safeDecimalToNumber(proto.getTotal())

  const isPriceVisible = primaryAmount !== 0 || total !== 0

  return {
    items: jobScopeItems.map((name) => ({ name })),
    price: !isPriceVisible
      ? null
      : {
          primaryAmount,
          upfrontAmount,
          total,
          discount: coupon
            ? {
                couponId: coupon.getId(),
                couponCode: coupon.getCode(),
                amount: safeDecimalToNumber(coupon.getAmount()),
              }
            : null,
        },
  }
}
